import api from './apiClient';
import axios from 'axios';
import type { StreamChannel } from './channelsService';

export interface PlayKastrChannelOverview {
  channel_id: number;
  channel_name: string;
  user_id: number;
  addon_id?: number | null;
  media_summary: {
    total: number;
    ready: number;
    error: number;
  };
  playlist_count: number;
  primary_playlist_id?: number | null;
  active_session_id?: number | null;
}

export interface PlayKastrPlaylistItem {
  id: number;
  hashId?: string;
  itemHashId?: string;
  item_hash_id?: string;
  position: number;
  kind: string;
  asset_id?: number | null;
  source_url?: string | null;
  source_label?: string | null;
  expected_duration_secs?: number | null;
  asset_label?: string | null;
  epg_title?: string | null;
  epg_category?: string | null;
  epg_description?: string | null;
  epg_type?: string | null;
  epg_age_rating?: string | null;
  epg_genre?: string | null;
  duration_secs?: number | null;
  trim_in_secs?: number | null;
  trim_out_secs?: number | null;
  scheduled_start?: string | null;
  calculated_start_utc?: string | null;
  calculated_end_utc?: string | null;
  exact_duration_ms?: number | null;
  timing_type?: string | null;
  asset_classification?: string | null;
  is_current?: boolean;
  is_next?: boolean;
  is_locked?: boolean;
  protected_boundary?: boolean;
  can_drag?: boolean;
  can_move_up?: boolean;
  can_move_down?: boolean;
  can_remove?: boolean;
  can_start_from?: boolean;
  timing_decision?: 'normal' | 'block_filled' | 'trimmed_for_boundary' | 'protected' | string | null;
  timing_reason?: string | null;
  boundary_time?: string | null;
  on_overrun?: string;
  transition?: string;
  is_protected?: boolean;
}

export interface PlayKastrPlaylist {
  id: number;
  hashId?: string;
  playlistHashId?: string;
  playlist_hash_id?: string;
  addon_id: number;
  name: string;
  mode: 'SEQUENTIAL' | 'CLOCK' | 'LOOP';
  timezone: string;
  next_playlist_id?: number | null;
  nextPlaylistHashId?: string | null;
  next_playlist_hash_id?: string | null;
  start_date?: string | null;
  clock_start?: string | null;
  recurrence?: string | null;
  weekdays?: number | null;
  items: PlayKastrPlaylistItem[];
}

export interface PlayKastrSessionView {
  id: number;
  playlistId?: number | null;
  playlist_id?: number | null;
  hashId?: string;
  sessionHashId?: string;
  session_hash_id?: string;
  status: string;
  output_url?: string | null;
  started_at?: string | null;
  error_message?: string | null;
}

export interface PlayKastrPlaylistSummary {
  id: number;
  hashId?: string;
  playlistHashId?: string;
  playlist_hash_id?: string;
  name: string;
  mode?: 'SEQUENTIAL' | 'CLOCK' | 'LOOP' | string;
  timezone?: string;
  next_playlist_id?: number | null;
  nextPlaylistHashId?: string | null;
  next_playlist_hash_id?: string | null;
  start_date?: string | null;
  clock_start?: string | null;
  recurrence?: string | null;
  weekdays?: number | null;
}

export interface PlayKastrRuntimeSnapshot {
  session?: PlayKastrSessionView | null;
  runtime?: {
    status: string;
    elapsed_secs: number;
    current_item_secs?: number;
    current_item_pos: number;
    current_item_id?: number | null;
    current_label?: string | null;
    started_at?: string | null;
    last_progress_at?: string | null;
  } | null;
  now_playing?: {
    item_id: number;
    label?: string | null;
    duration_secs?: number | null;
  } | null;
  up_next: Array<{
    item_id: number;
    label?: string | null;
    duration_secs?: number | null;
  }>;
  output_url?: string | null;
  loaded_playlist_updated_at?: string | null;
  current_playlist_updated_at?: string | null;
  pending_changes?: boolean;
  can_apply_on_next_loop?: boolean;
  requires_restart?: boolean;
  apply_mode?: string;
  apply_description?: string;
}

export interface PlayKastrGeneratedSnapshot {
  id: number;
  snapshotHashId?: string;
  playlistId: number;
  playlistHashId?: string;
  generatedAt: string;
  playlistUpdatedAt: string;
  itemCount: number;
  durationSecs: number;
  checksum: string;
}

export interface PlayKastrStudioData {
  playlist: PlayKastrPlaylist;
  media: Array<{
    id: number;
    label: string;
    norm_status: string;
    duration_secs?: number | null;
  }>;
  playlists: PlayKastrPlaylistSummary[];
  active_session?: PlayKastrSessionView | null;
  active_playout?: PlayKastrActivePlayout | null;
  snapshot?: PlayKastrRuntimeSnapshot | null;
  default_output_url: string;
  frozen_zone_minutes?: number;
  frozen_zone_seconds?: number;
  rundown_locked?: boolean;
  rundown_lock_reason?: string | null;
  protected_boundary_item_id?: number | null;
  timing_summary?: {
    recurrence: 'daily' | 'weekly' | string;
    target_window_secs: number;
    current_playlist_secs: number;
    attached_gap_playlist_secs: number;
    block_filler_secs?: number;
    combined_playlist_secs: number;
    gap_delta_secs: number;
    attached_gap_playlist_id?: number | null;
  } | null;
}

export interface PlayKastrActivePlayout {
  session?: PlayKastrSessionView | null;
  playlist?: PlayKastrPlaylistSummary | null;
  snapshot?: PlayKastrRuntimeSnapshot | null;
}

export interface PlayKastrAsset {
  id: number;
  uuid: string;
  label: string;
  normStatus: string;
  normProgress?: number | null;
  assetClass?: string;
  epgTitleDefault?: string | null;
  epgCategoryDefault?: string | null;
  epgSynopsisDefault?: string | null;
  epgTypeDefault?: string | null;
  epgAgeRatingDefault?: string | null;
  epgGenreDefault?: string | null;
  folderId?: number | null;
  folderName?: string | null;
  normError?: string | null;
  durationSecs?: number | null;
  thumbnailPath?: string | null;
  createdAt?: string;
}

export interface PlayKastrUploadJob {
  id: number;
  addon_id: number;
  asset_id?: number | null;
  file_name: string;
  label?: string | null;
  status: 'UPLOADING' | 'PROCESSING' | 'READY' | 'ERROR' | string;
  bytes_received: number;
  total_bytes?: number | null;
  progress_pct: number;
  error_message?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlayKastrFolder {
  id: number;
  addonId: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlayKastrAssetListResponse {
  items: PlayKastrAsset[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PlayKastrFolderListResponse {
  items: PlayKastrFolder[];
}

export interface PlayKastrUploadJobListResponse {
  items: PlayKastrUploadJob[];
}

export interface PlayKastrMediaInfo {
  id: number;
  label: string;
  assetClass?: string | null;
  durationSecs?: number | null;
  width?: number | null;
  height?: number | null;
  fps?: string | null;
  videoCodec?: string | null;
  audioCodec?: string | null;
  sampleRate?: number | null;
  audioChannels?: number | null;
  bitrate?: number | null;
  fileSizeBytes?: string | null;
  normStatus?: string | null;
  folderName?: string | null;
  type?: string | null;
  genre?: string | null;
  ageRating?: string | null;
  synopsis?: string | null;
  title?: string | null;
}

export interface PlayKastrLogEntry {
  id: number;
  action: string;
  details?: any;
  createdAt: string;
}

export interface PlayKastrLogListResponse {
  items: PlayKastrLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  range: string;
}

export interface PlayKastrAssignedDestination {
  id: number;
  transcoderHashId?: string;
  platformDistributor: string;
  protocol: string;
  destination: string;
  isActive: boolean;
  deliveryMode: 'PUSH' | 'PULL';
}

export interface PlayKastrWorkspaceResponse {
  playkastrActive: boolean;
  channelId: number;
  channelName: string;
  overview: PlayKastrChannelOverview | null;
  studio: PlayKastrStudioData | null;
  activePlayout?: PlayKastrActivePlayout | null;
  assignedDestination?: PlayKastrAssignedDestination | null;
  eligibleDestinations?: PlayKastrAssignedDestination[];
}

export interface PlayKastrPlayoutStatusResponse {
  playkastrActive: boolean;
  channelId: number;
  channelName: string;
  activePlayout?: PlayKastrActivePlayout | null;
  active_playout?: PlayKastrActivePlayout | null;
}

export interface PlayKastrPlaylistMutationItem {
  id?: number;
  position: number;
  kind: string;
  assetId?: number | null;
  sourceUrl?: string | null;
  sourceLabel?: string | null;
  expectedDurationSecs?: number | null;
  epgType?: string | null;
  epgAgeRating?: string | null;
  epgGenre?: string | null;
  trimInSecs?: number | null;
  trimOutSecs?: number | null;
  scheduledStart?: string | null;
  timingType?: string | null;
  onOverrun?: string;
  transition?: string;
  isProtected?: boolean;
}

const resolveChannelRouteId = (channel: StreamChannel | number | string) => {
  if (channel && typeof channel === 'object') {
    return String(channel.channelHashId || channel.hashId || channel.id || '');
  }
  return String(channel);
};

export type PlayKastrPlaylistRouteTarget = {
  playlistHashId?: string | null;
  playlist_hash_id?: string | null;
  hashId?: string | null;
  id?: number | string | null;
};

export type PlayKastrIdRef = number | string | PlayKastrPlaylistRouteTarget;

export const resolvePlayKastrPlaylistRouteId = (playlist?: PlayKastrIdRef | null) => {
  if (playlist == null) return '';
  if (typeof playlist === 'number' || typeof playlist === 'string') return String(playlist);
  return String(playlist.playlistHashId || playlist.playlist_hash_id || playlist.hashId || playlist.id || '');
};

export const resolvePlayKastrPlaylistHashRouteId = (playlist?: PlayKastrPlaylistRouteTarget | null) => {
  if (playlist == null || typeof playlist === 'number' || typeof playlist === 'string') return '';
  return String(playlist.playlistHashId || playlist.playlist_hash_id || playlist.hashId || '');
};

export const resolvePlayKastrItemRouteId = (item?: { itemHashId?: string | null; item_hash_id?: string | null; hashId?: string | null; id?: number | string | null } | number | string | null) => {
  if (item == null) return '';
  if (typeof item === 'number' || typeof item === 'string') return String(item);
  return String(item.itemHashId || item.item_hash_id || item.hashId || item.id || '');
};

export const resolvePlayKastrSessionRouteId = (session?: { sessionHashId?: string | null; session_hash_id?: string | null; hashId?: string | null; id?: number | string | null } | number | string | null) => {
  if (session == null) return '';
  if (typeof session === 'number' || typeof session === 'string') return String(session);
  return String(session.sessionHashId || session.session_hash_id || session.hashId || session.id || '');
};

export const getPlayKastrWorkspace = async (channel: StreamChannel | number | string, playlistId?: PlayKastrIdRef | null) =>
  api.get<PlayKastrWorkspaceResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/workspace`, {
    params: playlistId ? { playlistId: resolvePlayKastrPlaylistRouteId(playlistId) } : undefined,
  }).then((res) => res.data);

export const getPlayKastrPlayoutStatus = async (channel: StreamChannel | number | string) =>
  api.get<PlayKastrPlayoutStatusResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/playout-status`).then((res) => res.data);

export const getPlayKastrDestinationAssignment = async (channel: StreamChannel | number | string) =>
  api.get<{
    assignedDestination: PlayKastrAssignedDestination | null;
    eligibleDestinations: PlayKastrAssignedDestination[];
  }>(`/playkastr/channels/${resolveChannelRouteId(channel)}/destination-assignment`).then((res) => res.data);

export const setPlayKastrDestinationAssignment = async (
  channel: StreamChannel | number | string,
  transcoderId?: number | string | null
) =>
  api.put<{
    assignedDestination: PlayKastrAssignedDestination | null;
    eligibleDestinations: PlayKastrAssignedDestination[];
  }>(`/playkastr/channels/${resolveChannelRouteId(channel)}/destination-assignment`, {
    transcoderId: transcoderId ?? null,
  }).then((res) => res.data);

export const listPlayKastrAssets = async (channel: StreamChannel | number | string) =>
  api.get<PlayKastrAssetListResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets`).then((res) => res.data);

export const listPlayKastrFolders = async (channel: StreamChannel | number | string) =>
  api.get<PlayKastrFolderListResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/folders`).then((res) => res.data);

export const listPlayKastrUploadJobs = async (channel: StreamChannel | number | string) =>
  api.get<PlayKastrUploadJobListResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/upload-jobs`).then((res) => res.data);

export const cancelPlayKastrUploadJob = async (channel: StreamChannel | number | string, jobId: number) =>
  api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/upload-jobs/${jobId}`).then((res) => res.data);

export const listPlayKastrLogs = async (
  channel: StreamChannel | number | string,
  range: string = '24h',
  page: number = 1,
) => api.get<PlayKastrLogListResponse>(`/playkastr/channels/${resolveChannelRouteId(channel)}/logs`, {
  params: { range, page },
}).then((res) => res.data);

export const downloadPlayKastrLogsCsv = async (
  channel: StreamChannel | number | string,
  range: string = '24h',
) => api.get<Blob>(`/playkastr/channels/${resolveChannelRouteId(channel)}/logs.csv`, {
  params: { range },
  responseType: 'blob',
}).then((res) => res.data);

export const downloadPlayKastrTechnicalReport = async (
  channel: StreamChannel | number | string,
) => api.get<Blob>(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/report.csv`, {
  responseType: 'blob',
}).then((res) => res.data);

export const getPlayKastrMediaInfo = async (
  channel: StreamChannel | number | string,
  assetId: number,
) => api.get<PlayKastrMediaInfo>(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}/media-info`).then((res) => res.data);

export const getPlayKastrAssetStreamToken = async (
  channel: StreamChannel | number | string,
  assetId: number,
) => api.get<{ token: string; expiresIn: number }>(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}/stream-token`).then((res) => res.data);

export const buildPlayKastrAssetStreamUrl = (
  channel: StreamChannel | number | string,
  assetId: number,
  token: string,
) => `/api/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}/file?previewToken=${encodeURIComponent(token)}`;

export const uploadPlayKastrAsset = async (
  channel: StreamChannel | number | string,
  file: File,
  label?: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append('file', file);
  if (label) formData.append('label', label);

  const routeId = resolveChannelRouteId(channel);
  const { data: grant } = await api.post<{ url: string; maxBytes: number }>(`/uploads/channels/${routeId}/grants`, {
    target: 'playkaster',
    fileName: file.name,
    fileSize: file.size,
  });
  if (file.size > Number(grant.maxBytes || 0)) {
    throw new Error('This file exceeds the available PlayKaster storage capacity.');
  }
  const response = await axios.post<PlayKastrUploadJob>(grant.url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
    signal,
    onUploadProgress: (event) => {
      if (!event.total || !onProgress) return;
      onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
    },
  });

  return response.data;
};

export const createPlayKastrFolder = async (channel: StreamChannel | number | string, name: string) =>
  api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/folders`, { name }).then((res) => res.data);

export const renamePlayKastrFolder = async (channel: StreamChannel | number | string, folderId: number, name: string) =>
  api.put(`/playkastr/channels/${resolveChannelRouteId(channel)}/folders/${folderId}`, { name }).then((res) => res.data);

export const deletePlayKastrFolder = async (channel: StreamChannel | number | string, folderId: number) =>
  api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/folders/${folderId}`).then((res) => res.data);

export const updatePlayKastrAsset = async (
  channel: StreamChannel | number | string,
  assetId: number,
  payload: {
    label?: string;
    folderId?: number | null;
    assetClass?: string;
    epgTitleDefault?: string | null;
    epgCategoryDefault?: string | null;
    epgSynopsisDefault?: string | null;
    epgTypeDefault?: string | null;
    epgAgeRatingDefault?: string | null;
    epgGenreDefault?: string | null;
  },
) => api.patch(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}`, payload).then((res) => res.data);

export const deletePlayKastrAsset = async (channel: StreamChannel | number | string, assetId: number) =>
  api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}`).then((res) => res.data);

export const renormalisePlayKastrAsset = async (channel: StreamChannel | number | string, assetId: number) =>
  api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}/renormalise`).then((res) => res.data);

export const cleanupPlayKastrAssetOriginal = async (channel: StreamChannel | number | string, assetId: number) =>
  api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/assets/${assetId}/cleanup-original`).then((res) => res.data);

export const createPlayKastrPlaylist = async (
  channel: StreamChannel | number | string,
  payload: {
    name: string;
    mode?: 'SEQUENTIAL' | 'CLOCK' | 'LOOP';
    timezone?: string;
    nextPlaylistId?: PlayKastrIdRef | null;
    startDate?: string | null;
    clockStart?: string | null;
    recurrence?: string | null;
    weekdays?: number | null;
  },
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists`, payload).then((res) => res.data);

export const updatePlayKastrPlaylist = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  payload: {
    name?: string;
    mode?: 'SEQUENTIAL' | 'CLOCK' | 'LOOP';
    timezone?: string;
    nextPlaylistId?: PlayKastrIdRef | null;
    startDate?: string | null;
    clockStart?: string | null;
    recurrence?: string | null;
    weekdays?: number | null;
  },
) => api.put(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}`, payload).then((res) => res.data);

export const generatePlayKastrPlaylistSnapshot = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
) => api.post<PlayKastrGeneratedSnapshot>(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/generate-snapshot`).then((res) => res.data);

export const archivePlayKastrPlaylist = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
) => api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}`).then((res) => res.data);

export const replacePlayKastrPlaylistItems = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  items: PlayKastrPlaylistMutationItem[],
) => api.put(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items`, { items }).then((res) => res.data);

export interface AppendPlayKastrPlaylistItemOptions {
  position?: number | null;
  scheduledStart?: string | null;
}

export const appendPlayKastrPlaylistItem = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  assetId: number,
  kind: string = 'ASSET',
  positionOrOptions?: number | null | AppendPlayKastrPlaylistItemOptions,
) => {
  const options = typeof positionOrOptions === 'object' && positionOrOptions !== null
    ? positionOrOptions
    : { position: positionOrOptions };
  return api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items`, {
    assetId,
    kind,
    position: options.position ?? undefined,
    scheduledStart: options.scheduledStart ?? undefined,
  }).then((res) => res.data);
};

export const appendPlayKastrLiveItem = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  payload: {
    sourceUrl: string;
    sourceLabel?: string | null;
    expectedDurationSecs?: number | null;
    epgType?: string | null;
    epgGenre?: string | null;
    epgAgeRating?: string | null;
    epgTitle?: string | null;
    position?: number | null;
  },
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items`, {
  kind: 'LIVE',
  sourceUrl: payload.sourceUrl,
  sourceLabel: payload.sourceLabel ?? null,
  expectedDurationSecs: payload.expectedDurationSecs ?? null,
  epgType: payload.epgType ?? null,
  epgGenre: payload.epgGenre ?? null,
  epgAgeRating: payload.epgAgeRating ?? null,
  epgTitle: payload.epgTitle ?? null,
  position: payload.position ?? undefined,
  }).then((res) => res.data);

export const movePlayKastrPlaylistItem = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  itemId: PlayKastrIdRef,
  direction: 'up' | 'down' = 'up',
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items/${resolvePlayKastrItemRouteId(itemId)}/move`, { direction }).then((res) => res.data);

export const startPlayKastrPlaylistFromItem = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  itemId: PlayKastrIdRef,
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items/${resolvePlayKastrItemRouteId(itemId)}/start-from`).then((res) => res.data);

export const removePlayKastrPlaylistItem = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  itemId: PlayKastrIdRef,
) => api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items/${resolvePlayKastrItemRouteId(itemId)}`).then((res) => res.data);

export const removePlayKastrPlaylistItems = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  itemIds: PlayKastrIdRef[],
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items/delete-selected`, { itemIds }).then((res) => res.data);

export const clearPlayKastrPlaylist = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
) => api.delete(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items`).then((res) => res.data);

export const populatePlayKastrPlaylistBlock = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
) => api.post(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/populate`).then((res) => res.data);

export const updatePlayKastrPlaylistItemEpg = async (
  channel: StreamChannel | number | string,
  playlistId: PlayKastrIdRef,
  itemId: PlayKastrIdRef,
  payload: { title?: string | null; category?: string | null; synopsis?: string | null },
) => api.patch(`/playkastr/channels/${resolveChannelRouteId(channel)}/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/items/${resolvePlayKastrItemRouteId(itemId)}/epg`, payload).then((res) => res.data);

export const startPlayKastrPlayout = async (playlistId: PlayKastrIdRef, outputUrl?: string | null) =>
  api.post(`/playkastr/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/start`, outputUrl ? { outputUrl } : {}).then((res) => res.data);

export const continuePlayKastrPlayout = async (playlistId: PlayKastrIdRef) =>
  api.post(`/playkastr/playlists/${resolvePlayKastrPlaylistRouteId(playlistId)}/continue`).then((res) => res.data);

export const stopPlayKastrSession = async (sessionId: PlayKastrIdRef) =>
  api.post(`/playkastr/sessions/${resolvePlayKastrSessionRouteId(sessionId)}/stop`).then((res) => res.data);

export const reloadPlayKastrSession = async (sessionId: PlayKastrIdRef) =>
  api.post(`/playkastr/sessions/${resolvePlayKastrSessionRouteId(sessionId)}/reload`).then((res) => res.data);

export const applyPlayKastrPendingChanges = async (sessionId: PlayKastrIdRef) =>
  api.post<PlayKastrRuntimeSnapshot>(`/playkastr/sessions/${resolvePlayKastrSessionRouteId(sessionId)}/apply-pending`).then((res) => res.data);

export const skipPlayKastrSessionItem = async (sessionId: PlayKastrIdRef) =>
  api.post(`/playkastr/sessions/${resolvePlayKastrSessionRouteId(sessionId)}/skip`).then((res) => res.data);

export const getPlayKastrXmltvPreview = async (
  channel: StreamChannel | number | string,
  playlistId?: PlayKastrIdRef | null,
) => api.get<string>(`/playkastr/channels/${resolveChannelRouteId(channel)}/epg/xmltv`, {
  params: playlistId ? { playlistId: resolvePlayKastrPlaylistRouteId(playlistId) } : undefined,
  responseType: 'text' as const,
}).then((res) => res.data);

export const downloadPlayKastrXmltv = async (
  channel: StreamChannel | number | string,
  playlistId?: PlayKastrIdRef | null,
) => api.get<Blob>(`/playkastr/channels/${resolveChannelRouteId(channel)}/epg/xmltv`, {
  params: { ...(playlistId ? { playlistId: resolvePlayKastrPlaylistRouteId(playlistId) } : {}), download: 1 },
  responseType: 'blob',
}).then((res) => res.data);

export const getPlayKastrGracenotePreview = async (
  channel: StreamChannel | number | string,
  playlistId?: PlayKastrIdRef | null,
) => api.get(`/playkastr/channels/${resolveChannelRouteId(channel)}/epg/gracenote`, {
  params: playlistId ? { playlistId: resolvePlayKastrPlaylistRouteId(playlistId) } : undefined,
}).then((res) => res.data);

export const downloadPlayKastrGracenote = async (
  channel: StreamChannel | number | string,
  playlistId?: PlayKastrIdRef | null,
) => api.get<Blob>(`/playkastr/channels/${resolveChannelRouteId(channel)}/epg/gracenote`, {
  params: { ...(playlistId ? { playlistId: resolvePlayKastrPlaylistRouteId(playlistId) } : {}), download: 1 },
  responseType: 'blob',
}).then((res) => res.data);
